import { useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faBoxArchive, faChevronLeft, faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCustomFields } from 'api/customFields';
import Content from 'assets/js/components/Content';
import * as ArchiveCustomFieldDialog from 'components/custom-fields/ArchiveCustomFieldDialog';
import { customFieldTypeToReadable, typeIcon } from 'components/custom-fields/helpers';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { TableRowDropdownTrigger } from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { customFieldQueryKeys } from 'queries/custom-field-query-keys';
import { CustomField, CustomFieldGroup } from 'types/domain';

const columnHelper = createColumnHelper<CustomField>();

const ContactCustomFields = () => {
    const history = useHistory();
    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search, 250);
    const { accountId } = useCurrentAccount();

    const { expanded, pagination, setPagination, setExpanded, sorting, setSorting } =
        Table.useReactTableState({
            sortingState: [{ id: 'order', desc: false }],
        });

    const [isPending, params] = useIsPending(
        {
            ...(searchTerm ? { query: searchTerm } : {}),
            ...(sorting.length > 0
                ? { [`sortBy${sorting[0].desc ? 'Desc' : ''}`]: sorting[0].id }
                : {}),
            page: pagination.pageIndex + 1,
            group: 'contact' as CustomFieldGroup,
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: customFieldQueryKeys.customFieldsWithFilters(accountId, params),
        queryFn: () => fetchCustomFields(accountId, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('title', {
                header: 'Label',
                enableSorting: true,
                cell: info => {
                    return (
                        <p className={'flex-shrink font-medium text-black hover:underline'}>
                            {info.getValue()}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('type', {
                header: 'Field Type',
                enableSorting: false,
                cell: info => (
                    <div className={'flex items-center gap-x-6'}>
                        <div className={'flex items-end space-x-2 pr-6 font-medium'}>
                            <FontAwesomeIcon icon={typeIcon(info.getValue())} className={'w-5'} />
                            <span className={'leading-3'}>
                                {customFieldTypeToReadable(info.getValue())}
                            </span>
                        </div>
                    </div>
                ),
            }),
            columnHelper.display({
                id: 'metadata',
                header: 'Contact type',
                cell: ({ row }) => {
                    const metadata = row.original.metadata;
                    return (
                        <div className="flex gap-4">
                            {metadata?.show_create && (
                                <div
                                    className={
                                        'w-fit rounded-full bg-gray-100 px-2 py-1 text-sm leading-none'
                                    }
                                >
                                    Individuals
                                </div>
                            )}
                            {metadata?.show_create_company && (
                                <div
                                    className={
                                        'w-fit rounded-full bg-gray-100 px-2 py-1 text-sm leading-none'
                                    }
                                >
                                    Companies
                                </div>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: ({ row }) => (
                    <DropdownMenu.Root key={row.original.id}>
                        <TableRowDropdownTrigger />
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <DropdownMenu.Item
                                    onClick={e => {
                                        e.stopPropagation();

                                        const customFieldId = row.original.id;

                                        history.push(
                                            `/accounts/${accountId}/contacts/custom-fields/${customFieldId}/edit`,
                                        );
                                    }}
                                >
                                    Edit field
                                </DropdownMenu.Item>
                                <ArchiveCustomFieldDialog.Root customField={row.original}>
                                    <ArchiveCustomFieldDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            <FontAwesomeIcon
                                                icon={faBoxArchive}
                                                className={'mr-2'}
                                            />
                                            Archive
                                        </DropdownMenu.Item>
                                    </ArchiveCustomFieldDialog.Trigger>
                                </ArchiveCustomFieldDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                ),
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [accountId, history],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
            sorting,
        },
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to: cell =>
                `/accounts/${accountId}/contacts/custom-fields/${cell.row.original.id}/edit`,
        },
    });

    useSyncPaginatedData(table, data);

    return (
        <div className={'flex flex-col gap-y-6'}>
            <Link className={'w-fit'} to={`/accounts/${accountId}/contacts`}>
                <Button variant="none" size="sm" className="text-gray-600">
                    <FontAwesomeIcon size={'sm'} icon={faChevronLeft} />
                    Back to contacts
                </Button>
            </Link>

            <header className={'flex max-w-prose flex-col gap-y-1'}>
                <h1 className={'text-2xl font-medium'}>Custom Fields</h1>
                <p className={'text-base leading-normal text-gray-700'}>
                    Create new field types for contacts, allowing you to capture and store specific
                    data about donors, supporters, and volunteers.
                </p>
            </header>

            <div className={'flex flex-col gap-y-4'}>
                <div className={'flex justify-between'}>
                    <Input.Root>
                        <FontAwesomeIcon icon={faSearch} className={'mr-2 text-gray-300'} />
                        <Input.Form
                            value={search}
                            placeholder={'Search fields'}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </Input.Root>
                    <Button
                        variant="solid"
                        onClick={() =>
                            history.push(`/accounts/${accountId}/contacts/custom-fields/new`)
                        }
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New field
                    </Button>
                </div>

                <div className={'flex flex-col gap-y-4'}>
                    <Table.Root
                        isLoading={isPending}
                        table={table}
                        searchTerm={searchTerm}
                        variant="bordered"
                    >
                        <Box className="overflow-x-auto">
                            <Table.Content>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'primary'}>
                                <Table.EmptyIcon />
                                <Table.EmptyTitle>No custom fields</Table.EmptyTitle>
                                <Table.EmptyDescription>
                                    Create your first custom field.
                                </Table.EmptyDescription>
                                <Table.EmptyWhenNotSearching>
                                    <Button
                                        variant="solid"
                                        onClick={() =>
                                            history.push(
                                                `/accounts/${accountId}/contacts/custom-fields/new`,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        New field
                                    </Button>
                                </Table.EmptyWhenNotSearching>
                            </Table.Empty>
                        </Box>
                        <Table.Pagination>
                            <Table.PaginationPageNavigation />
                        </Table.Pagination>
                    </Table.Root>
                </div>
            </div>
        </div>
    );
};

const Wrapper = () => (
    <Content>
        <ContactCustomFields />
    </Content>
);

export { Wrapper as ContactCustomFields };
